import { useEffect, useRef, useCallback } from 'react';
import { Editor, useEditorState } from '@tiptap/react';
import {
  Minus,
  Trash2,
  Columns,
  Rows3,
  Merge,
  SplitSquareHorizontal,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
} from 'lucide-react';

interface TableBubbleMenuProps {
  editor: Editor;
}

export default function TableBubbleMenu({ editor }: TableBubbleMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  // Use useEditorState to reactively track whether cursor is in a table
  const isInTable = useEditorState({
    editor,
    selector: (ctx) => ctx.editor?.isActive('table') ?? false,
  });

  // Position the menu above the table whenever visibility or selection changes
  const updatePosition = useCallback(() => {
    if (!isInTable || !menuRef.current || !editor || editor.isDestroyed) return;

    try {
      const { selection } = editor.state;
      const domAtPos = editor.view.domAtPos(selection.from);
      const domNode = domAtPos.node instanceof HTMLElement ? domAtPos.node : domAtPos.node.parentElement;
      const tableEl = domNode?.closest('table');

      if (tableEl && menuRef.current) {
        const container = menuRef.current.offsetParent as HTMLElement;
        if (container) {
          const containerRect = container.getBoundingClientRect();
          const tableRect = tableEl.getBoundingClientRect();
          const top = tableRect.top - containerRect.top - 44;
          menuRef.current.style.top = `${Math.max(0, top)}px`;
          menuRef.current.style.left = `${Math.max(0, tableRect.left - containerRect.left)}px`;
        }
      }
    } catch {
      // domAtPos can throw if position is invalid
    }
  }, [isInTable, editor]);

  useEffect(() => {
    if (isInTable) {
      // Use rAF to ensure DOM is up to date
      requestAnimationFrame(updatePosition);
    }
  }, [isInTable, updatePosition]);

  // Also reposition on every selection update while visible
  useEffect(() => {
    if (!editor || editor.isDestroyed || !isInTable) return;
    const handler = () => requestAnimationFrame(updatePosition);
    editor.on('selectionUpdate', handler);
    return () => { editor.off('selectionUpdate', handler); };
  }, [editor, isInTable, updatePosition]);

  const btnBase =
    'px-2 py-1 rounded text-xs font-medium transition-colors text-white/80 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1';
  const btnDanger =
    'px-2 py-1 rounded text-xs font-medium transition-colors text-red-400 hover:text-red-300 hover:bg-red-400/10 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1';

  return (
    <div
      ref={menuRef}
      className="absolute z-50 flex items-center gap-0.5 bg-[#1A1A1A] border border-white/10 rounded-lg p-1 shadow-2xl transition-opacity duration-150"
      style={{
        top: 0,
        left: 0,
        minWidth: 'max-content',
        opacity: isInTable ? 1 : 0,
        visibility: isInTable ? 'visible' : 'hidden',
        pointerEvents: isInTable ? 'auto' : 'none',
      }}
    >
      {/* Row Operations */}
      <button
        onClick={() => editor.chain().focus().addRowBefore().run()}
        disabled={!editor.can().addRowBefore()}
        className={btnBase}
        title="Add Row Above"
      >
        <ArrowUp className="w-3 h-3" />
        <Rows3 className="w-3 h-3" />
      </button>
      <button
        onClick={() => editor.chain().focus().addRowAfter().run()}
        disabled={!editor.can().addRowAfter()}
        className={btnBase}
        title="Add Row Below"
      >
        <ArrowDown className="w-3 h-3" />
        <Rows3 className="w-3 h-3" />
      </button>
      <button
        onClick={() => editor.chain().focus().deleteRow().run()}
        disabled={!editor.can().deleteRow()}
        className={btnDanger}
        title="Delete Row"
      >
        <Minus className="w-3 h-3" />
        <Rows3 className="w-3 h-3" />
      </button>

      <div className="w-px h-5 bg-white/10 mx-0.5" />

      {/* Column Operations */}
      <button
        onClick={() => editor.chain().focus().addColumnBefore().run()}
        disabled={!editor.can().addColumnBefore()}
        className={btnBase}
        title="Add Column Left"
      >
        <ArrowLeft className="w-3 h-3" />
        <Columns className="w-3 h-3" />
      </button>
      <button
        onClick={() => editor.chain().focus().addColumnAfter().run()}
        disabled={!editor.can().addColumnAfter()}
        className={btnBase}
        title="Add Column Right"
      >
        <ArrowRight className="w-3 h-3" />
        <Columns className="w-3 h-3" />
      </button>
      <button
        onClick={() => editor.chain().focus().deleteColumn().run()}
        disabled={!editor.can().deleteColumn()}
        className={btnDanger}
        title="Delete Column"
      >
        <Minus className="w-3 h-3" />
        <Columns className="w-3 h-3" />
      </button>

      <div className="w-px h-5 bg-white/10 mx-0.5" />

      {/* Merge / Split */}
      <button
        onClick={() => editor.chain().focus().mergeCells().run()}
        disabled={!editor.can().mergeCells()}
        className={btnBase}
        title="Merge Cells"
      >
        <Merge className="w-3 h-3" />
      </button>
      <button
        onClick={() => editor.chain().focus().splitCell().run()}
        disabled={!editor.can().splitCell()}
        className={btnBase}
        title="Split Cell"
      >
        <SplitSquareHorizontal className="w-3 h-3" />
      </button>

      <div className="w-px h-5 bg-white/10 mx-0.5" />

      {/* Delete Table */}
      <button
        onClick={() => editor.chain().focus().deleteTable().run()}
        disabled={!editor.can().deleteTable()}
        className={btnDanger}
        title="Delete Table"
      >
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  );
}
